// imports
import { Controller, useFormContext } from 'react-hook-form';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, MenuItem, TextField } from '@mui/material';
import { KeyboardArrowDownRounded as KeyboardArrowDownRoundedIcon } from '@mui/icons-material';
//interfaces , constants , helper
import { renderExcipient } from 'lib/helper';
import { HTTP_STATUS } from 'constants/index';
import { ExcipientSelectorProps, SelectType } from 'interfaces';
import { ExcipientsPayload, useFindAllExcipientSelectorLazyQuery } from 'generated/graphql';

const ExcipientSelector: FC<ExcipientSelectorProps> = ({
  name,
  title,
  disabled,
  isRequired,
  defaultValue,
  handleChange,
  margin = 'dense',
  isMultiple = false,
  carriers = [],
}) => {
  const [options, setOptions] = useState<ExcipientsPayload['data']>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { control } = useFormContext();

  const [findAllExcipients, { loading }] = useFindAllExcipientSelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllExcipients } = data;
      const { data: excipients, response } = findAllExcipients || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setOptions(excipients as ExcipientsPayload['data']);
      }
    },
    onError: () => {
      setOptions([]);
    },
  });

  const fetchAllExcipients = useCallback(async () => {
    await findAllExcipients({
      variables: {
        findAllExcipientsInput: {
          paginationOptions: { page: 1, limit: 10 },
          search: searchQuery,
          isActive: true,
        },
      },
    });
  }, [findAllExcipients, searchQuery]);

  useEffect(() => {
    (!searchQuery.length || searchQuery.length > 1) && fetchAllExcipients();
  }, [fetchAllExcipients, searchQuery.length]);

  const menuOptions = useMemo(() => {
    const updatedOptions = [...renderExcipient(options ?? [])];

    if (carriers?.length) {
      return updatedOptions.filter(
        (option) => !carriers.some((carrier) => carrier.excipient?.name === option.name),
      );
    }

    return updatedOptions;
  }, [options, carriers]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { name, onBlur, onChange, value, ref } = field;
        const { error } = fieldState;
        const { message } = error || {};
        return (
          <Autocomplete
            ref={ref}
            multiple={isMultiple}
            onBlur={onBlur}
            disableClearable
            disabled={disabled}
            options={menuOptions}
            value={isMultiple ? (value as SelectType[]) : (value as SelectType)}
            onChange={(_, value) => {
              onChange(value);
              handleChange && handleChange(value);
            }}
            popupIcon={<KeyboardArrowDownRoundedIcon />}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={({ value: option }, { value }) => value === option}
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                margin={margin}
                variant="outlined"
                helperText={message}
                error={Boolean(error)}
                defaultValue={defaultValue}
                label={isRequired ? `${title} *` : title}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}>
                {menuOptions?.map((item) => {
                  const { name, value } = item;
                  return (
                    <MenuItem key={value} value={value}>
                      {name}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
          />
        );
      }}
    />
  );
};

export default ExcipientSelector;
